import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  FiEdit2, FiX, FiSave, FiTrash2, FiPlus, FiChevronDown, FiChevronUp, FiClipboard, FiCopy, FiCheck, FiLayers, FiKey, FiUploadCloud,
} from "react-icons/fi";
import { usePermission } from "../../context/PermissionContext";
import AdsSectionCard, { Switch, Spinner } from "./AdsSectionCard";
import {
  AD_PLATFORMS,
  AD_UNIT_PATTERN,
  PLACEMENT_KEY_PATTERN,
  groupKeys,
  unitsFor,
  parseUnitsJson,
  filledCount,
  toGoogleAdsJson,
  copyText,
  fetchAdsNetworks,
  createAdsNetwork,
  updateAdsNetwork,
  toggleAdsNetwork,
  deleteAdsNetwork,
  fetchAdPlacements,
  createAdPlacement,
  renameAdPlacement,
  deleteAdPlacement,
} from "../../utils/adsNetworkApi";

const KEY_RULE = "Letters, digits and _ only, starting with a letter (e.g. banner_profile).";

/* ─── Add / Edit network modal ─── */
const NetworkModal = ({ initial, keys, canAddKeys, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  // Keys already in the list, plus keys added in this form (created on Save).
  const [knownKeys, setKnownKeys] = useState(keys);
  const [newKeys, setNewKeys] = useState([]);
  const allKeys = [...knownKeys, ...newKeys];

  const [name, setName] = useState(initial?.name || "");
  const [units, setUnits] = useState(() => ({
    android: unitsFor(initial?.android, keys),
    ios: unitsFor(initial?.ios, keys),
  }));
  const [keyDraft, setKeyDraft] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [saving, setSaving] = useState(false);

  const setUnit = (platform, key, value) => setUnits((u) => ({ ...u, [platform]: { ...u[platform], [key]: value } }));

  const hasKey = (key) => allKeys.some((k) => k.toLowerCase() === key.toLowerCase());

  const addKeys = (list) => {
    const fresh = [];
    for (const key of list) {
      if (!hasKey(key) && !fresh.some((k) => k.toLowerCase() === key.toLowerCase())) fresh.push(key);
    }
    if (fresh.length) setNewKeys((prev) => [...prev, ...fresh]);
    return fresh;
  };

  const handleAddKey = () => {
    const key = keyDraft.trim();
    if (!PLACEMENT_KEY_PATTERN.test(key)) {
      toast.error(`"${key}" is not a valid key. ${KEY_RULE}`);
      return;
    }
    if (hasKey(key)) {
      toast.error(`"${key}" is already in the list`);
      return;
    }
    addKeys([key]);
    setKeyDraft("");
  };

  const removeNewKey = (key) => {
    setNewKeys((prev) => prev.filter((k) => k !== key));
    setUnits((u) => {
      const next = { android: { ...u.android }, ios: { ...u.ios } };
      delete next.android[key];
      delete next.ios[key];
      return next;
    });
  };

  const applyPaste = () => {
    const parsed = parseUnitsJson(pasteText);
    if (!parsed) {
      toast.error('Could not read that JSON. Paste the "googleAds" object, or "android" / "ios" maps.');
      return;
    }
    const pastedKeys = [...new Set([...Object.keys(parsed.android), ...Object.keys(parsed.ios)])];
    const unknown = pastedKeys.filter((k) => !hasKey(k));
    const malformed = unknown.filter((k) => !PLACEMENT_KEY_PATTERN.test(k));
    const addable = unknown.filter((k) => PLACEMENT_KEY_PATTERN.test(k));
    const skipped = [...malformed, ...(canAddKeys ? [] : addable)];
    const added = canAddKeys ? addKeys(addable) : [];
    const keep = (map) => Object.fromEntries(Object.entries(map).filter(([k]) => !skipped.includes(k)));

    setUnits((u) => ({ android: { ...u.android, ...keep(parsed.android) }, ios: { ...u.ios, ...keep(parsed.ios) } }));
    setShowPaste(false);
    setPasteText("");
    toast.success(added.length ? `IDs filled from JSON — ${added.length} new key(s) added: ${added.join(", ")}` : "IDs filled from JSON");
    if (skipped.length) toast.error(`Skipped key(s) that cannot be added here: ${skipped.join(", ")}`);
  };

  const invalid = AD_PLATFORMS.flatMap((p) =>
    allKeys.filter((k) => !AD_UNIT_PATTERN.test((units[p.key][k] || "").trim())).map((k) => `${p.label} ${k}`)
  );

  const handleSave = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      toast.error("Network name must be at least 2 characters.");
      return;
    }
    if (invalid.length) {
      toast.error(`${invalid[0]}: remove spaces, quotes, backslash or < >.`);
      return;
    }
    setSaving(true);
    try {
      // New keys go into the list first; every network gets them.
      const created = [];
      try {
        for (const key of newKeys) {
          await createAdPlacement(key);
          created.push(key);
        }
      } finally {
        if (created.length) {
          setKnownKeys((prev) => [...prev, ...created]);
          setNewKeys((prev) => prev.filter((k) => !created.includes(k)));
        }
      }
      const payload = { name: trimmed, android: unitsFor(units.android, allKeys), ios: unitsFor(units.ios, allKeys) };
      if (isEdit) await updateAdsNetwork(initial.id, payload);
      else await createAdsNetwork(payload);
      toast.success(isEdit ? "Ads network updated!" : "Ads network added!");
      onSaved();
    } catch (err) {
      toast.error(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form
        onSubmit={handleSave}
        className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <h3 className="text-base font-semibold text-gray-800">{isEdit ? "Edit Ads Network" : "Add Ads Network"}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600"><FiX size={18} /></button>
        </div>

        <div className="px-6 py-5 overflow-y-auto space-y-5">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1">
              <label htmlFor="ads-network-name" className="block text-xs font-medium text-gray-500 mb-1">Network Name</label>
              <input
                id="ads-network-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Google AdMob, GameAdZone, Jungle"
                maxLength={64}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowPaste((v) => !v)}
                className="flex items-center justify-center gap-1.5 text-xs font-medium text-blue-600 border border-blue-100 hover:bg-blue-50 px-3 py-2.5 rounded-lg transition"
              >
                <FiClipboard size={13} /> {showPaste ? "Hide JSON paste" : "Paste from JSON"}
              </button>
              <CopyJsonButton
                getText={() => toGoogleAdsJson(units, allKeys)}
                what={name.trim() ? `${name.trim()}'s IDs (as typed)` : "the IDs (as typed)"}
                className="flex items-center justify-center gap-1.5 text-xs font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 px-3 py-2.5 rounded-lg transition"
              />
            </div>
          </div>

          {showPaste && (
            <div className="space-y-2 rounded-xl border border-blue-100 bg-blue-50/40 p-3">
              <textarea
                aria-label="Ad unit JSON"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                rows={7}
                placeholder={'Paste the API response, or "googleAds": { "android": { "banner_general": "…" }, "ios": { … } }'}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] text-gray-500">
                  Keys not in the list yet are added as new keys{canAddKeys ? "" : " — you need the create permission for that"}.
                </p>
                <button
                  type="button"
                  onClick={applyPaste}
                  className="text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition"
                >
                  Fill fields
                </button>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-gray-100 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="text-left text-gray-400 uppercase tracking-wide bg-gray-50">
                  <th className="py-2 px-3 font-medium w-56">Key</th>
                  {AD_PLATFORMS.map((p) => (
                    <th key={p.key} className="py-2 px-3 font-medium">{p.label}</th>
                  ))}
                </tr>
              </thead>
              {groupKeys(allKeys).map((group) => (
                <tbody key={group.label} className="divide-y divide-gray-50">
                  <tr>
                    <td colSpan={3} className="px-3 pt-3 pb-1 text-[11px] font-semibold text-gray-500 uppercase tracking-wide bg-white">
                      {group.label} <span className="text-gray-300 font-normal">· {group.keys.length}</span>
                    </td>
                  </tr>
                  {group.keys.map((key) => {
                    const isNew = newKeys.includes(key);
                    return (
                      <tr key={key} className={isNew ? "bg-blue-50/40" : ""}>
                        <td className="py-1.5 px-3 align-top">
                          <span className="flex items-center gap-1.5 pt-2">
                            <span className="font-mono text-gray-700 break-all">{key}</span>
                            {isNew && (
                              <>
                                <span className="text-[10px] font-semibold uppercase bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">New</span>
                                <button type="button" onClick={() => removeNewKey(key)} aria-label={`Remove ${key}`} className="text-gray-400 hover:text-red-500">
                                  <FiX size={12} />
                                </button>
                              </>
                            )}
                          </span>
                        </td>
                        {AD_PLATFORMS.map((p) => {
                          const value = units[p.key][key] || "";
                          const bad = !AD_UNIT_PATTERN.test(value.trim());
                          return (
                            <td key={p.key} className="py-1.5 px-3 align-top">
                              <input
                                type="text"
                                aria-label={`${p.label} ${key}`}
                                aria-invalid={bad}
                                value={value}
                                onChange={(e) => setUnit(p.key, key, e.target.value)}
                                placeholder="Ad unit ID"
                                maxLength={191}
                                className={`w-full px-2.5 py-1.5 border rounded-md text-xs font-mono focus:outline-none focus:ring-2 ${
                                  bad ? "border-red-300 focus:ring-red-200" : "border-gray-200 focus:ring-blue-300"
                                }`}
                              />
                              {bad && <p className="text-[11px] text-red-500 mt-0.5">No spaces, quotes, backslash or &lt; &gt;.</p>}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
            {allKeys.length === 0 && <p className="py-6 text-center text-gray-400 text-xs">No keys yet — add one below.</p>}
          </div>

          {canAddKeys && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <label htmlFor="ads-new-key" className="text-xs font-medium text-gray-500 whitespace-nowrap">New key</label>
              <input
                id="ads-new-key"
                type="text"
                value={keyDraft}
                onChange={(e) => setKeyDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddKey();
                  }
                }}
                placeholder="e.g. banner_profile"
                maxLength={64}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              <button
                type="button"
                onClick={handleAddKey}
                className="flex items-center justify-center gap-1.5 text-xs font-medium text-blue-600 border border-blue-200 hover:bg-blue-50 px-3 py-2 rounded-lg transition"
              >
                <FiPlus size={13} /> Add key
              </button>
            </div>
          )}
          <p className="text-xs text-gray-400">
            Paste each ID exactly as the network gives it — any format works. Leave a field empty if this network has no
            unit for it; the app gets "". A new key is added for every network.
          </p>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium py-2.5 rounded-lg transition"
          >
            <FiSave size={14} />
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Network"}
          </button>
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

/* ─── Import JSON: keys and IDs in one go ─── */
const NEW_NETWORK = "new";

const ImportModal = ({ networks, keys, canCreate, canEdit, onClose, onDone }) => {
  const editable = canEdit ? networks : [];
  const [text, setText] = useState("");
  const [target, setTarget] = useState(() => {
    const preferred = editable.find((n) => n.isActive) || editable[0];
    return preferred ? String(preferred.id) : NEW_NETWORK;
  });
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const handleImport = async (e) => {
    e.preventDefault();
    const parsed = parseUnitsJson(text);
    if (!parsed) {
      toast.error('Could not read that JSON. Paste the API response, the "googleAds" object, or "android" / "ios" maps.');
      return;
    }
    const pastedKeys = [...new Set([...Object.keys(parsed.android), ...Object.keys(parsed.ios)])];
    const malformed = pastedKeys.filter((k) => !PLACEMENT_KEY_PATTERN.test(k));
    if (malformed.length) {
      toast.error(`Not valid key(s): ${malformed.join(", ")}. ${KEY_RULE}`);
      return;
    }
    const badIds = AD_PLATFORMS.flatMap((p) =>
      Object.entries(parsed[p.key]).filter(([, v]) => !AD_UNIT_PATTERN.test(v)).map(([k]) => `${p.label} ${k}`)
    );
    if (badIds.length) {
      toast.error(`${badIds[0]}: remove spaces, quotes, backslash or < >.`);
      return;
    }
    const known = new Map(keys.map((k) => [k.toLowerCase(), k]));
    const unknown = pastedKeys.filter((k) => !known.has(k.toLowerCase()));
    if (unknown.length && !canCreate) {
      toast.error(`New key(s) need the create permission: ${unknown.join(", ")}`);
      return;
    }
    const isNew = target === NEW_NETWORK;
    const network = isNew ? null : networks.find((n) => String(n.id) === target);
    const name = isNew ? newName.trim() : network?.name;
    if (!name || name.length < 2) {
      toast.error("Network name must be at least 2 characters.");
      return;
    }

    setBusy(true);
    try {
      for (const key of unknown) await createAdPlacement(key);
      const allKeys = [...keys, ...unknown];
      // A pasted key that differs only in case from a known one goes to the known key.
      const canonical = (map) => Object.fromEntries(Object.entries(map).map(([k, v]) => [known.get(k.toLowerCase()) || k, v]));
      const merge = (platform) => ({ ...unitsFor(network?.[platform], allKeys), ...canonical(parsed[platform]) });
      const payload = { name, android: unitsFor(merge("android"), allKeys), ios: unitsFor(merge("ios"), allKeys) };
      if (isNew) await createAdsNetwork(payload);
      else await updateAdsNetwork(network.id, payload);

      const count = Object.keys(parsed.android).length + Object.keys(parsed.ios).length;
      toast.success(
        `Imported ${count} ID(s) into ${name}` + (unknown.length ? ` — ${unknown.length} new key(s) added: ${unknown.join(", ")}` : "")
      );
      onDone();
    } catch (err) {
      toast.error(err.message || "Import failed");
      // Keys created before the failure stay; reload so the page shows them.
      onDone({ keepOpen: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <form onSubmit={handleImport} className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-800">Import keys &amp; IDs from JSON</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600"><FiX size={18} /></button>
        </div>
        <p className="text-xs text-gray-500">
          Paste the app's JSON — the whole API response works. Every key and ID in it is set in one go: new keys are added
          for every network, and IDs not in the JSON stay as they are.
        </p>
        <textarea
          aria-label="JSON to import"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          placeholder={'{ "status": true, "data": { "googleAds": { "android": { "banner_general": "…" }, "ios": { … } } } }'}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-300"
        />
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label htmlFor="import-target" className="block text-xs font-medium text-gray-500 mb-1">Into network</label>
            <select
              id="import-target"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              {editable.map((n) => (
                <option key={n.id} value={String(n.id)}>{n.name}{n.isActive ? " (active)" : ""}</option>
              ))}
              {canCreate && <option value={NEW_NETWORK}>+ New network…</option>}
            </select>
          </div>
          {target === NEW_NETWORK && (
            <div className="flex-1">
              <label htmlFor="import-new-name" className="block text-xs font-medium text-gray-500 mb-1">New network name</label>
              <input
                id="import-new-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. GameAdZone"
                maxLength={64}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <button
            type="submit"
            disabled={busy || !text.trim()}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium py-2.5 rounded-lg transition"
          >
            <FiUploadCloud size={14} /> {busy ? "Importing..." : "Import"}
          </button>
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

/* ─── Copy a network's IDs as JSON ─── */
const CopyJsonButton = ({ getText, what, className }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (await copyText(getText())) {
      toast.success(`Copied ${what} as JSON`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error("Could not copy — your browser blocked the clipboard.");
    }
  };

  return (
    <button type="button" onClick={copy} className={className}>
      {copied ? <FiCheck size={13} className="text-green-600" /> : <FiCopy size={13} />} {copied ? "Copied" : "Copy JSON"}
    </button>
  );
};

/* ─── One ID with a copy button ─── */
const UnitId = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-gray-300">— not set</span>;

  const copy = async () => {
    if (await copyText(value)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error("Could not copy — select the ID instead.");
    }
  };

  return (
    <span className="flex items-start gap-1.5 min-w-0">
      <span className="font-mono text-gray-700 break-all">{value}</span>
      <button type="button" onClick={copy} aria-label={`Copy ${label}`} title="Copy" className="shrink-0 text-gray-400 hover:text-blue-600 p-0.5 rounded transition">
        {copied ? <FiCheck size={12} className="text-green-600" /> : <FiCopy size={12} />}
      </button>
    </span>
  );
};

/* ─── Read-only ID table for one network ─── */
const UnitsTable = ({ network, keys }) => (
  <div className="mx-5 mb-4 rounded-lg border border-gray-100 bg-white overflow-x-auto">
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-gray-400 uppercase tracking-wide bg-gray-50">
          <th className="py-2 px-3 font-medium w-56">Key</th>
          {AD_PLATFORMS.map((p) => (
            <th key={p.key} className="py-2 px-3 font-medium">{p.label}</th>
          ))}
        </tr>
      </thead>
      {groupKeys(keys).map((group) => (
        <tbody key={group.label} className="divide-y divide-gray-50">
          <tr>
            <td colSpan={3} className="px-3 pt-3 pb-1 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{group.label}</td>
          </tr>
          {group.keys.map((key) => (
            <tr key={key}>
              <td className="py-2 px-3 font-mono text-gray-500 break-all">{key}</td>
              {AD_PLATFORMS.map((p) => (
                <td key={p.key} className="py-2 px-3">
                  <UnitId value={network[p.key]?.[key]} label={`${p.label} ${key}`} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      ))}
    </table>
  </div>
);

/* ─── Ad placement keys card ─── */
const PlacementKeysCard = ({ placements, canCreate, canEdit, canDelete, onChanged }) => {
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(null); // { id, key, value }
  const [busyId, setBusyId] = useState(null);

  const handleAdd = async (e) => {
    e.preventDefault();
    const key = draft.trim();
    if (!PLACEMENT_KEY_PATTERN.test(key)) {
      toast.error(`"${key}" is not a valid key. ${KEY_RULE}`);
      return;
    }
    setAdding(true);
    try {
      await createAdPlacement(key);
      toast.success(`Key "${key}" added — every network now has it, empty until you fill it in`);
      setDraft("");
      onChanged();
    } catch (err) {
      toast.error(err.message || "Failed to add the key");
    } finally {
      setAdding(false);
    }
  };

  const handleRename = async (e) => {
    e.preventDefault();
    const key = renaming.value.trim();
    if (key === renaming.key) return setRenaming(null);
    if (!PLACEMENT_KEY_PATTERN.test(key)) {
      toast.error(`"${key}" is not a valid key. ${KEY_RULE}`);
      return;
    }
    setBusyId(renaming.id);
    try {
      await renameAdPlacement(renaming.id, key);
      toast.success(`Renamed to "${key}" in every network`);
      setRenaming(null);
      onChanged();
    } catch (err) {
      toast.error(err.message || "Failed to rename the key");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (placement) => {
    if (!window.confirm(`Delete the key "${placement.key}"?\n\nThe app stops receiving it, and its ID is removed from every network.`)) return;
    setBusyId(placement.id);
    try {
      await deleteAdPlacement(placement.id);
      toast.success(`Key "${placement.key}" deleted`);
      onChanged();
    } catch (err) {
      toast.error(err.message || "Failed to delete the key");
    } finally {
      setBusyId(null);
    }
  };

  const byKey = Object.fromEntries(placements.map((p) => [p.key, p]));

  return (
    <AdsSectionCard
      icon={FiKey}
      iconClass="bg-amber-50 text-amber-600"
      title="Ad placement keys"
      description={
        <>
          The keys the app receives in <span className="font-mono">googleAds.android</span> / <span className="font-mono">googleAds.ios</span>.
          Every network has every key — a new key is sent at once, as "" until you fill it in on a network.
        </>
      }
    >
      {canCreate && (
        <form onSubmit={handleAdd} className="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3">
          <input
            aria-label="New placement key"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. banner_profile"
            maxLength={64}
            className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
          <button
            type="submit"
            disabled={adding || !draft.trim()}
            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition whitespace-nowrap"
          >
            <FiPlus size={14} /> {adding ? "Adding..." : "Add Key"}
          </button>
        </form>
      )}

      {placements.length === 0 ? (
        <div className="py-10 text-center text-gray-400 text-sm">No keys yet — the app gets empty googleAds objects.</div>
      ) : (
        <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {groupKeys(placements.map((p) => p.key)).map((group) => (
            <div key={group.label} className="rounded-xl border border-gray-100">
              <p className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-50">
                {group.label} <span className="text-gray-300 font-normal">· {group.keys.length}</span>
              </p>
              <ul className="divide-y divide-gray-50">
                {group.keys.map((key) => {
                  const placement = byKey[key];
                  const editing = renaming?.id === placement.id;
                  return (
                    <li key={placement.id} className="flex items-center gap-2 px-3 py-1.5 min-h-[36px]">
                      {editing ? (
                        <form onSubmit={handleRename} className="flex flex-1 items-center gap-1.5">
                          <input
                            aria-label={`New name for ${key}`}
                            value={renaming.value}
                            onChange={(e) => setRenaming({ ...renaming, value: e.target.value })}
                            autoFocus
                            maxLength={64}
                            className="flex-1 min-w-0 px-2 py-1 border border-gray-200 rounded text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-300"
                          />
                          <button type="submit" disabled={busyId === placement.id} aria-label="Save name" className="text-green-600 hover:bg-green-50 p-1 rounded"><FiCheck size={13} /></button>
                          <button type="button" onClick={() => setRenaming(null)} aria-label="Cancel rename" className="text-gray-400 hover:bg-gray-100 p-1 rounded"><FiX size={13} /></button>
                        </form>
                      ) : (
                        <>
                          <span className="flex-1 min-w-0 font-mono text-xs text-gray-700 break-all">{key}</span>
                          {canEdit && (
                            <button
                              onClick={() => setRenaming({ id: placement.id, key, value: key })}
                              aria-label={`Rename ${key}`}
                              title="Rename — the app reads keys by name, so rename together with an app update"
                              className="text-gray-400 hover:text-amber-600 hover:bg-amber-50 p-1 rounded"
                            >
                              <FiEdit2 size={12} />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(placement)}
                              disabled={busyId === placement.id}
                              aria-label={`Delete ${key}`}
                              className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded disabled:opacity-50"
                            >
                              <FiTrash2 size={12} />
                            </button>
                          )}
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
      {canEdit && placements.length > 0 && (
        <p className="px-5 pb-4 text-[11px] text-gray-400">The app reads keys by name — rename or delete one only together with an app update.</p>
      )}
    </AdsSectionCard>
  );
};

/* ─── Main section ─── */
const GoogleAdsNetworks = ({ onActiveChange }) => {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("setting", "create");
  const canEdit = hasPermission("setting", "edit");
  const canDelete = hasPermission("setting", "delete");

  const [networks, setNetworks] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false); // first load done — later reloads keep the content on screen
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null); // null | {} (add) | network (edit)
  const [importing, setImporting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const keys = placements.map((p) => p.key);
  const totalIds = keys.length * AD_PLATFORMS.length;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [netRes, keyRes] = await Promise.all([fetchAdsNetworks(), fetchAdPlacements()]);
      setNetworks(netRes.data || []);
      setPlacements(keyRes.data || []);
      setLoaded(true);
      setError("");
    } catch (err) {
      setError(err.message || "Failed to fetch ads networks");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Tell the page which network is on, for its summary.
  useEffect(() => {
    if (loaded && !error && onActiveChange) onActiveChange(networks.find((n) => n.isActive) || null);
  }, [networks, loaded, error, onActiveChange]);

  const handleToggle = async (network) => {
    setTogglingId(network.id);
    try {
      await toggleAdsNetwork(network.id);
      toast.success(network.isActive ? `${network.name} switched off` : `${network.name} is now the active network`);
      // Switching one on switches the others off — reflect that locally.
      setNetworks((prev) =>
        prev.map((n) =>
          n.id === network.id ? { ...n, isActive: !network.isActive } : network.isActive ? n : { ...n, isActive: false }
        )
      );
    } catch (err) {
      toast.error(err.message || "Failed to toggle");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (network) => {
    if (!window.confirm(`Delete the "${network.name}" ads network?`)) return;
    setDeletingId(network.id);
    try {
      await deleteAdsNetwork(network.id);
      toast.success("Ads network deleted!");
      setNetworks((prev) => prev.filter((n) => n.id !== network.id));
    } catch (err) {
      toast.error(err.message || "Failed to delete");
    } finally {
      setDeletingId(null);
    }
  };

  const active = networks.find((n) => n.isActive);

  return (
    <>
      <AdsSectionCard
        icon={FiLayers}
        iconClass="bg-red-50 text-red-500"
        title="Google Ads networks"
        description={
          <>
            <p>
              AdMob, GameAdZone, Jungle… Switch one on and the app uses its Android and iOS ad unit IDs
              (<span className="font-mono">adsSetting.googleAds</span>). Only one can be on at a time.
            </p>
            {loaded && !error && (
              <p className="mt-1">
                {active ? (
                  <span className="text-green-600 font-medium">Active: {active.name}</span>
                ) : (
                  <span className="text-amber-600 font-medium">No network active — the app gets empty ad unit IDs.</span>
                )}
              </p>
            )}
          </>
        }
        action={
          (canCreate || canEdit) && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setImporting(true)}
                disabled={!loaded || !!error || (!canCreate && networks.length === 0)}
                className="flex items-center gap-2 border border-blue-200 text-blue-600 hover:bg-blue-50 disabled:opacity-60 text-sm font-medium px-4 py-2 rounded-lg transition whitespace-nowrap"
              >
                <FiUploadCloud size={14} /> Import JSON
              </button>
              {canCreate && (
                <button
                  onClick={() => setModal({})}
                  disabled={!loaded || !!error}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-lg transition whitespace-nowrap"
                >
                  <FiPlus size={14} /> Add Network
                </button>
              )}
            </div>
          )
        }
      >
        {!loaded && loading ? (
          <Spinner />
        ) : error ? (
          <div className="m-5 bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-sm">{error}</div>
        ) : networks.length === 0 ? (
          <div className="py-10 text-center text-gray-400 text-sm">No networks yet. Add AdMob, GameAdZone, Jungle…</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {networks.map((network) => {
              const expanded = expandedId === network.id;
              const filled = filledCount(network, keys);
              return (
                <div key={network.id} data-testid={`ads-network-${network.id}`} className={network.isActive ? "bg-green-50/40" : ""}>
                  <div className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 flex items-center gap-2 min-w-0">
                        <span className="truncate">{network.name}</span>
                        {network.isActive && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Live</span>
                        )}
                      </p>
                      <p className={`text-xs ${filled === totalIds ? "text-gray-400" : "text-amber-600"}`}>
                        {filled}/{totalIds} ad unit IDs set
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <Switch
                        checked={network.isActive}
                        label={`${network.name} active`}
                        disabled={!canEdit || togglingId === network.id}
                        onChange={() => handleToggle(network)}
                      />
                      <span className={`text-xs font-medium w-14 ${network.isActive ? "text-green-600" : "text-gray-400"}`}>
                        {network.isActive ? "Active" : "Inactive"}
                      </span>
                      <button
                        onClick={() => setExpandedId(expanded ? null : network.id)}
                        aria-expanded={expanded}
                        className="flex items-center gap-1 text-xs text-gray-600 hover:bg-gray-100 px-2.5 py-1.5 rounded-lg transition"
                      >
                        {expanded ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />} {expanded ? "Hide IDs" : "View IDs"}
                      </button>
                      <CopyJsonButton
                        getText={() => toGoogleAdsJson(network, keys)}
                        what={`${network.name}'s IDs`}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:bg-blue-50 px-2.5 py-1.5 rounded-lg transition"
                      />
                      {canEdit && (
                        <button
                          onClick={() => setModal(network)}
                          className="flex items-center gap-1 text-xs text-amber-600 hover:bg-amber-50 px-2.5 py-1.5 rounded-lg transition"
                        >
                          <FiEdit2 size={13} /> Edit
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(network)}
                          disabled={network.isActive || deletingId === network.id}
                          title={network.isActive ? "Switch another network on before deleting the active one" : undefined}
                          className="flex items-center gap-1 text-xs text-red-500 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition disabled:opacity-40 disabled:hover:bg-transparent"
                        >
                          <FiTrash2 size={13} /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                  {expanded && <UnitsTable network={network} keys={keys} />}
                </div>
              );
            })}
          </div>
        )}

        {modal && (
          <NetworkModal
            initial={modal.id ? modal : null}
            keys={keys}
            canAddKeys={canCreate}
            onClose={() => setModal(null)}
            onSaved={() => {
              setModal(null);
              load();
            }}
          />
        )}

        {importing && (
          <ImportModal
            networks={networks}
            keys={keys}
            canCreate={canCreate}
            canEdit={canEdit}
            onClose={() => setImporting(false)}
            onDone={(opts) => {
              if (!opts?.keepOpen) setImporting(false);
              load();
            }}
          />
        )}
      </AdsSectionCard>

      {loaded && !error && (
        <PlacementKeysCard placements={placements} canCreate={canCreate} canEdit={canEdit} canDelete={canDelete} onChanged={load} />
      )}
    </>
  );
};

export default GoogleAdsNetworks;
